import saveAs from 'file-saver/dist/FileSaver';
import { PricesAndChartsEndpoints } from '@/services/shared/ApiEndpoints';
import { BaseService } from '@/services/shared/BaseService';
import {
  AveragePriceDto,
  AverageSpotPriceResponse,
  IntradayPoint,
  PriceDto,
} from '@/types/PricesAndCharts';
import { API } from '@/utils/api';

class PricesAndChartsService {
  async fetchByContractAndRangeOrDate(
    contract: string,
    range?: string,
    startDate?: string,
    endDate?: string
  ): Promise<{ data: any; upperLimit: any; lowerLimit: any }> {
    const params: any = {};
    if (range) {
      params.range = range;
    }
    if (startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    }
    const response = await BaseService.handleApiRequest<{
      prices: PriceDto[];
      upperLimit: number;
      lowerLimit: number;
    }>('get', PricesAndChartsEndpoints.GET_BY_DATE_RANGE(contract), { params });
    return {
      data: response.prices,
      lowerLimit: response.lowerLimit,
      upperLimit: response.upperLimit,
    };
  }

  async fetchAverageSpot(
    startDate: string,
    endDate: string,
    contracts?: string[]
  ): Promise<AverageSpotPriceResponse> {
    const params: Record<string, any> = {
      startDate,
      endDate,
      contracts: contracts?.join(','),
    };
    return await BaseService.handleApiRequest<AverageSpotPriceResponse>(
      'get',
      PricesAndChartsEndpoints.GET_AVERAGE_SPOT,
      { params }
    );
  }

  async fetchSpotIntraday(
    granularity: 15 | 60,
    startDate: string,
    endDate: string
  ): Promise<IntradayPoint[]> {
    const params: Record<string, any> = { granularity, startDate, endDate };
    return await BaseService.handleApiRequest<IntradayPoint[]>(
      'get',
      PricesAndChartsEndpoints.GET_SPOT_INTRADAY,
      { params }
    );
  }

  async fetchMarketData(): Promise<
    {
      contractName: string;
      previousPrice: number;
      currentPrice: number;
      dailyChangePercentage: number;
      monthlyChangePercentage: number;
    }[]
  > {
    return await BaseService.handleApiRequest<
      {
        contractName: string;
        previousPrice: number;
        currentPrice: number;
        dailyChangePercentage: number;
        monthlyChangePercentage: number;
      }[]
    >('get', PricesAndChartsEndpoints.GET_MARKET);
  }

  async fetchContractsStructure(): Promise<any> {
    const response = await BaseService.handleApiRequest<any>(
      'get',
      PricesAndChartsEndpoints.STRUCTURE
    );
    return response.categories;
  }

  async fetchAvailableRanges(contract: string): Promise<string[]> {
    return await BaseService.handleApiRequest<string[]>(
      'get',
      PricesAndChartsEndpoints.GET_AVAILABLE_RANGES(contract)
    );
  }

  async downloadChartXls(
    contractCsv: string,
    startDate: string,
    endDate: string,
    fileLabel?: string
  ): Promise<{ missingContracts?: string[] }> {
    try {
      const fileName = `wykres_${fileLabel ?? contractCsv}.xlsx`;
      
      const response = await API.get(
        PricesAndChartsEndpoints.GENERATE_XLS(contractCsv),
        {
          params: { startDate, endDate },
          responseType: 'blob',
        }
      );

      saveAs(response.data, fileName);

      const missingContractsHeader = response.headers['x-missing-data-contracts'];
      const missingContracts = missingContractsHeader
        ? missingContractsHeader.split(',').map((c: string) => c.trim())
        : [];

      return {
        missingContracts: missingContracts.length > 0 ? missingContracts : undefined
      };
    } catch (error) {
      if (error.response?.data instanceof Blob) {
        throw new Error(await error.response.data.text());
      }
      throw new Error(error.message);
    }
  }
}

const instance = new PricesAndChartsService();
export { instance as PricesAndChartsService };
